import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Loader2, X, Globe, MapPin, Pencil, Sparkles, Plus } from "lucide-react";

const TABS = [
  { id: "website", label: "Website URL", icon: Globe,
    hint: "Paste any business website. Claude reads it and auto-fills name, phone, address, niche & more." },
  { id: "google", label: "Google Profile", icon: MapPin,
    hint: "Type a business name + city (or paste a Google Maps URL). We fetch verified data via Places API." },
  { id: "manual", label: "Manual", icon: Pencil,
    hint: "Type the data yourself. If you provide a website, AI fills in the rest." },
];

const BLANK = {
  // common
  campaign_id: "",
  // website
  website_url: "",
  // google
  google_query: "",
  // manual
  name: "", phone: "", email: "", address: "", city: "", state: "", niche: "", website: "",
  auto_enrich: true,
  auto_classify: true,
  auto_contact: false,
};

export default function AddLeadModal({ open, onClose, onCreated }) {
  const [tab, setTab] = useState("website");
  const [form, setForm] = useState(BLANK);
  const [submitting, setSubmitting] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    if (!open) return;
    setForm(BLANK);
    setTab("website");
    api.get("/campaigns").then(({ data }) => setCampaigns(data)).catch(() => {});
  }, [open]);

  if (!open) return null;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        mode: tab,
        campaign_id: form.campaign_id || null,
        auto_enrich: form.auto_enrich,
        auto_classify: form.auto_classify,
        auto_contact: form.auto_contact,
      };
      if (tab === "website") {
        if (!form.website_url.trim()) { toast.error("Website URL is required"); setSubmitting(false); return; }
        payload.website = form.website_url.trim();
      } else if (tab === "google") {
        if (!form.google_query.trim()) { toast.error("Business name or Google Maps URL is required"); setSubmitting(false); return; }
        payload.google_query = form.google_query.trim();
      } else {
        if (!form.name.trim()) { toast.error("Business name is required"); setSubmitting(false); return; }
        Object.assign(payload, {
          name: form.name.trim(),
          phone: form.phone.trim() || null,
          email: form.email.trim() || null,
          address: form.address.trim() || null,
          city: form.city.trim() || null,
          state: form.state.trim() || null,
          niche: form.niche.trim() || null,
          website: form.website.trim() || null,
        });
      }
      const { data } = await api.post("/leads/manual", payload);
      toast.success(`Lead created: ${data.name}`);
      onCreated?.(data);
      onClose();
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiError(e.response?.data?.detail) || "Failed to create lead";
      if (status === 409) toast.warning(detail);
      else if (status === 422 || status === 404) toast.error(detail);
      else toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const activeTab = TABS.find((t) => t.id === tab);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      data-testid="add-lead-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-zinc-300 shadow-xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="overline mb-0.5">/ add lead</div>
            <h2 className="font-heading text-xl font-black tracking-tight">Register a New Lead</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="add-lead-close"
            className="text-zinc-500 hover:text-zinc-900 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 flex gap-1 border-b border-zinc-100">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`add-lead-tab-${t.id}`}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 inline-flex items-center gap-1.5 transition ${
                  active
                    ? "border-[#002FA7] text-[#002FA7]"
                    : "border-transparent text-zinc-500 hover:text-zinc-900"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Hint */}
        <div className="px-6 py-3 bg-zinc-50 border-b border-zinc-100 flex items-start gap-2">
          <Sparkles className="w-3.5 h-3.5 mt-0.5 text-[#002FA7] shrink-0" />
          <p className="text-xs text-zinc-600">{activeTab.hint}</p>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {tab === "website" && (
            <Field label="Website URL" testid="field-website-url">
              <input
                type="url"
                value={form.website_url}
                onChange={(e) => set("website_url", e.target.value)}
                data-testid="input-website-url"
                placeholder="https://www.example-venue.com"
                className="w-full px-3 py-2.5 border border-zinc-300 text-sm font-mono focus:outline-none focus:border-[#002FA7]"
              />
            </Field>
          )}

          {tab === "google" && (
            <Field label="Business name or Google Maps URL" testid="field-google-query">
              <input
                value={form.google_query}
                onChange={(e) => set("google_query", e.target.value)}
                data-testid="input-google-query"
                placeholder="Magnolia Hotel Houston TX  —  or paste a maps.google.com URL"
                className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]"
              />
              <p className="text-xs text-zinc-400 mt-1 font-mono">
                Tip: include city + state for accurate matching.
              </p>
            </Field>
          )}

          {tab === "manual" && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Business Name *" testid="field-name">
                <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="input-name"
                  className="w-full px-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]" placeholder="Acme Wedding Co." />
              </Field>
              <Field label="Phone" testid="field-phone">
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="input-phone"
                  className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono focus:outline-none focus:border-[#002FA7]" placeholder="+1..." />
              </Field>
              <Field label="Email" testid="field-email">
                <input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email"
                  className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono focus:outline-none focus:border-[#002FA7]" placeholder="contact@..." />
              </Field>
              <Field label="Website" testid="field-manual-website">
                <input value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="input-manual-website"
                  className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono focus:outline-none focus:border-[#002FA7]" placeholder="https://..." />
              </Field>
              <Field label="Niche" testid="field-niche">
                <input value={form.niche} onChange={(e) => set("niche", e.target.value)} data-testid="input-niche"
                  className="w-full px-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]" placeholder="wedding venue" />
              </Field>
              <Field label="City" testid="field-city">
                <input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="input-city"
                  className="w-full px-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]" placeholder="Austin" />
              </Field>
              <Field label="State" testid="field-state">
                <input value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="input-state"
                  className="w-full px-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]" placeholder="TX" />
              </Field>
              <Field label="Address" testid="field-address">
                <input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="input-address"
                  className="w-full px-3 py-2 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]" placeholder="123 Main St" />
              </Field>
              <label className="col-span-2 flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={form.auto_enrich} onChange={(e) => set("auto_enrich", e.target.checked)} data-testid="input-auto-enrich" />
                <Sparkles className="w-3 h-3 text-[#002FA7]" />
                <span>If website is provided, auto-enrich email + socials + quality with AI</span>
              </label>
            </div>
          )}

          {/* Campaign attribution */}
          <Field label="Attribute to Campaign (optional)" testid="field-campaign">
            <select
              value={form.campaign_id}
              onChange={(e) => set("campaign_id", e.target.value)}
              data-testid="select-campaign"
              className="w-full px-3 py-2 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#002FA7]"
            >
              <option value="">— No campaign —</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>

          {/* AI + auto-contact flags */}
          <div className="border border-zinc-200 bg-zinc-50/60 px-4 py-3 space-y-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={form.auto_classify}
                onChange={(e) => set("auto_classify", e.target.checked)}
                data-testid="input-auto-classify"
              />
              <Sparkles className="w-3 h-3 text-[#002FA7]" />
              <span>AI score + generate SMS/call/email copy on create <span className="text-zinc-500">(recommended)</span></span>
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={form.auto_contact}
                onChange={(e) => set("auto_contact", e.target.checked)}
                data-testid="input-auto-contact"
              />
              <Sparkles className="w-3 h-3 text-amber-600" />
              <span>
                <strong>Auto-contact now</strong> — start the AI sequence immediately (SMS → call follow-up). Overrides the Settings toggle for this lead.
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-200 flex items-center justify-end gap-2 bg-zinc-50">
          <button
            onClick={onClose}
            data-testid="add-lead-cancel"
            className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-zinc-700 hover:bg-zinc-100"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="add-lead-submit"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {submitting ? "Creating…" : "Create Lead"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="overline block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
